import React, { useEffect, useMemo, useState } from "react";
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Button, Alert, Checkbox,
    TextField,
    InputAdornment,
    Card
} from "@mui/material";
import { CheckCircle, PendingActions, DoneAll, Search } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { setAlertData, startLoading, stopLoading } from "../../Redux/Screen/Action";
import dayjs from "dayjs";
import PaymentApi from "Service/Payment.api";
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { formatDate } from "Utils/Common";
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';


const SuperAdminApproval: React.FC = () => {
    const navigate = useNavigate();
    const [pendingList, setPendingList] = useState<any[]>([]);
    const [selectedIds, setSelectedIds] = useState<number[]>([]); // Track selections
    const [completedList, setCompletedList] = useState<any[]>([]);
    const [searchPendingQuery, setSearchPendingQuery] = useState("");
    const [searchCompletedQuery, setSearchCompletedQuery] = useState("");
    const dispatch = useDispatch();

    useEffect(() => {
        fetchPending();
        fetchCompleted();
    }, []);

    const fetchPending = async () => {
        dispatch(startLoading({ loading: true, loadtext: "Loading pending approvals..." }));
        try {
            const res = await PaymentApi.getPendingPayouts();
            setPendingList(res.data || []);
            setSelectedIds([]); // Reset selection on refresh
        } finally {
            dispatch(stopLoading());
        }
    };

    const fetchCompleted = async () => {
        dispatch(startLoading({ loading: true, loadtext: "Loading payouts..." }));
        try {
            const res = await PaymentApi.getCompletedPayouts();
            setCompletedList(res.data || []);
        } finally {
            dispatch(stopLoading());
        }
    };


    // Toggle single selection
    const handleSelect = (id: number) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
        );
    };

    // Toggle Select All
    const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {

        if (event.target.checked) {
            const allIds = pendingList.map(row => row.pkPayout);
            setSelectedIds(allIds);
        } else {
            setSelectedIds([]);
        }
    };

    // Unified Approve Function (Works for single and multiple)
    const processApproval = async (ids: number[]) => {
        const message = ids.length > 1
            ? `Confirm approval for ${ids.length} payouts?`
            : "Confirm this payout?";

        // if (!window.confirm(message)) return;

        dispatch(startLoading({ loading: true, loadtext: "Processing Approval..." }));
        try {
            // Assuming your API handles an array of IDs
            // If it only handles one, you might need a Promise.all or a different endpoint
            await PaymentApi.approvePayoutsBulk(ids);

        } catch (error) {
            // alert("Approval failed.");
        } finally {
            dispatch(stopLoading());
            dispatch(
                setAlertData({
                    alertMessage: "Payouts Approved Successfully",
                    alertVariant: "success",
                    openAlert: true,
                })
            );
            fetchPending();
            fetchCompleted();
        }
    };

    // 3. OPTIMIZED SEARCH: Pending Table
    const filteredPending = useMemo(() => {
        const query = searchPendingQuery.toLowerCase().trim();
        if (!query) return pendingList;
        return pendingList.filter(item =>
            item.professional?.toLowerCase().includes(query) ||
            item.caseTitle?.toLowerCase().includes(query) ||
            item.caseId?.toString().includes(query) ||
            item.amount?.toString().includes(query) ||
            formatDate(item.paymentDate).toLowerCase().includes(query)
        );
    }, [searchPendingQuery, pendingList]);

    // 4. OPTIMIZED SEARCH: Completed Table
    const filteredCompleted = useMemo(() => {
        const query = searchCompletedQuery.toLowerCase().trim();
        if (!query) return completedList;
        return completedList.filter(item =>
            item.professional?.toLowerCase().includes(query) ||
            item.caseTitle?.toLowerCase().includes(query) ||
            item.caseId?.toString().includes(query) ||
            item.amount?.toString().includes(query) ||
            formatDate(item.paymentDate).toLowerCase().includes(query) ||
            formatDate(item.completionDate).toLowerCase().includes(query)
        );
    }, [searchCompletedQuery, completedList]);

    const handleDownload = async () => {
        dispatch(startLoading({ loading: true, loadtext: "Generating Report..." }));
        try {
            const response = await PaymentApi.downloadCompletedPayouts();

            const blobData = response.data ? response.data : response;

            const blob = new Blob([blobData], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            link.setAttribute('download', `Completed_Payouts_${dayjs().format('YYYY-MM-DD')}.xlsx`);

            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Download failed", error);
        } finally {
            dispatch(stopLoading());
        }
    };

    // Calculate the sum of selected payouts in real-time
const totalSelectedValue = useMemo(() => {
    return pendingList
        .filter(item => selectedIds.includes(item.pkPayout))
        .reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
}, [selectedIds, pendingList]);

    return (
        <Box sx={{ height: '90vh', overflowY: 'auto', p: { xs: 2, sm: 4 }, bgcolor: '#f5f5f5' }}>
            {/* Top Header Section */}
            {/* 3. ADDED BACK BUTTON HERE */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Button 
                    startIcon={<ArrowBackIcon />} 
                    onClick={() => navigate(-1)}
                    sx={{ fontWeight: 'bold' }}
                >
                    Back
                </Button>
            </Box>
            <Box
                display="flex"
                flexDirection={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
                gap={2}
                mb={3}
            >
                <Box>
                    <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                        Payout Approval Queue
                    </Typography>
                    <Typography color="text.primary" variant="body2">
                        Review and finalize payout amounts entered by Admins.
                    </Typography>
                </Box>

                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<DoneAll />}
                    disabled={selectedIds.length === 0}
                    onClick={() => processApproval(selectedIds)}
                    sx={{
                        borderRadius: 2,
                        px: 4,
                        height: 50,
                        width: { xs: '100%', md: 'auto' } // Full width on mobile
                    }}
                >
                    Approve Selected ({selectedIds.length})
                </Button>
            </Box>

            <Alert severity="info" sx={{ mb: 3 }}>
                You can approve payouts individually or use the checkboxes to approve in bulk.
            </Alert>

 {/* Top Bar: Search and Stats in one line */}
<Box 
    display="flex" 
    flexDirection={{ xs: 'column', sm: 'row' }} 
    alignItems="center" 
    justifyContent="space-between" 
    gap={2} 
    mb={3}
>
    {/* Search for Pending Table */}
    <TextField
        value={searchPendingQuery}
        onChange={(e) => setSearchPendingQuery(e.target.value)}
        placeholder="Search pending..."
        size="small"
        sx={{
            width: { xs: '100%', sm: '300px' },
        }}
        InputProps={{
            startAdornment: (
                <InputAdornment position="start">
                    <SearchIcon />
                </InputAdornment>
            ),
        }}
    />

    {/* STATS SUMMARY - Now aligned to the right */}
    <Card sx={{
        p: 1.5,
        px: 4,
        minWidth: { xs: '100%', sm: 'auto' },
        textAlign: 'center',
        border: selectedIds.length > 0 ? '1px solid #283165' : '1px solid #e0e0e0',
        transition: 'all 0.3s ease',
        boxShadow: selectedIds.length > 0 ? 2 : 0,
        bgcolor: selectedIds.length > 0 ? '#f8f9ff' : 'white'
    }}>
        <Typography color="text.primary" variant="caption" fontWeight="bold" display="block">
            TOTAL SELECTED ({selectedIds.length})
        </Typography>
        <Typography variant="h5" color={selectedIds.length > 0 ? "primary.main" : "text.disabled"} fontWeight="bold">
            ₹{totalSelectedValue.toLocaleString('en-IN')}
        </Typography>
    </Card>
</Box>

            <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 3, maxHeight: 450, overflow: "auto" }}>
                <Table stickyHeader> {/* Added stickyHeader for better UX during scroll */}
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox" sx={{ bgcolor: "#283165" }}>
                                <Checkbox
                                    indeterminate={selectedIds.length > 0 && selectedIds.length < pendingList.length}
                                    checked={pendingList.length > 0 && selectedIds.length === pendingList.length}
                                    onChange={handleSelectAll}
                                    sx={{ color: "white", '&.Mui-checked': { color: "white" }, '&.MuiCheckbox-indeterminate': { color: "white" } }}
                                />
                            </TableCell>
                            <TableCell sx={{ bgcolor: "#283165", color: "white" }}>Professional</TableCell>
                            <TableCell sx={{ bgcolor: "#283165", color: "white" }}>Case Info</TableCell>
                            <TableCell sx={{ bgcolor: "#283165", color: "white" }}>Amount</TableCell>
                            <TableCell sx={{ bgcolor: "#283165", color: "white" }}>Date</TableCell>
                            <TableCell sx={{ bgcolor: "#283165", color: "white" }} align="center">Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredPending.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 10 }}>
                                    <PendingActions sx={{ fontSize: 40, color: "text.disabled" }} />
                                    <Typography color="text.primary">No payouts pending approval.</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredPending.map((row) => {
                                const isItemSelected = selectedIds.includes(row.pkPayment);
                                return (
                                    <TableRow key={row.pkPayment} hover selected={isItemSelected}>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                icon={<CheckBoxOutlineBlankIcon />}
                                                checkedIcon={<CheckBoxIcon />}
                                                checked={selectedIds.includes(row.pkPayout)}
                                                onChange={() => handleSelect(row.pkPayout)}
                                                sx={{
                                                    color: "#283165",
                                                    '&.Mui-checked': { color: "#283165" },
                                                    '& .MuiSvgIcon-root': { fontSize: 28 }
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography fontWeight="bold" variant="body2">{row.professional}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" display="block" fontWeight="bold">{row.caseTitle}</Typography>
                                            <Typography variant="caption" color="text.primary">ID: {row.caseId}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography color="primary.main" fontWeight="bold" variant="body2">₹{row.amount}</Typography>
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.75rem' }}>{dayjs(row.paymentDate).format("DD MMM YYYY")}</TableCell>
                                        <TableCell align="center">
                                            <Button
                                                variant="outlined"
                                                color="success"
                                                size="small"
                                                startIcon={<CheckCircle />}
                                                onClick={() => processApproval([row.pkPayout])}
                                                sx={{ textTransform: "none", borderRadius: 2, whiteSpace: 'nowrap' }}
                                            >
                                                Approve
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Completed Payouts Section */}
            <Box sx={{ mt: 6 }}>
                <Box
                    display="flex"
                    flexDirection={{ xs: 'column', md: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    gap={2}
                    sx={{ mb: 3 }}
                >
                    <Typography variant="h6" fontWeight="bold">
                        Completed Payouts
                    </Typography>

                    <Box
                        display="flex"
                        flexDirection={{ xs: 'column', sm: 'row' }}
                        alignItems="center"
                        gap={2}
                        sx={{ width: { xs: '100%', md: 'auto' } }}
                    >
                        <TextField
                            value={searchCompletedQuery}
                            onChange={(e) => setSearchCompletedQuery(e.target.value)}
                            placeholder="Search history..."
                            size="small"
                            sx={{ width: { xs: '100%', sm: '250px' } }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownload}
                            sx={{
                                whiteSpace: 'nowrap',
                                height: '40px',
                                width: { xs: '100%', sm: 'auto' }
                            }}
                        >
                            Download Report
                        </Button>
                    </Box>
                </Box>

                <TableContainer
                    component={Paper}
                    sx={{
                        maxHeight: 400,
                        overflow: "auto",
                        borderRadius: 2,
                        boxShadow: 3
                    }}
                >
                    <Table stickyHeader sx={{ minWidth: 650 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ bgcolor: "#283165", color: "white", fontWeight: "bold" }}>Professional</TableCell>
                                <TableCell sx={{ bgcolor: "#283165", color: "white", fontWeight: "bold" }}>Case Info</TableCell>
                                <TableCell sx={{ bgcolor: "#283165", color: "white", fontWeight: "bold" }}>Amount</TableCell>
                                <TableCell sx={{ bgcolor: "#283165", color: "white", fontWeight: "bold" }}>Submitted Date</TableCell>
                                <TableCell sx={{ bgcolor: "#283165", color: "white", fontWeight: "bold" }}>Completed Date</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody
                            sx={{
                                '& .MuiTableRow-root:nth-of-type(even)': { backgroundColor: '#f5f5f5' },
                                '& .MuiTableRow-root:hover': { backgroundColor: '#eeeeee !important' },
                            }}
                        >
                            {filteredCompleted.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                                        <Typography color="text.primary">No completed payouts available.</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredCompleted.map((row) => (
                                    <TableRow key={row.pkPayment} hover>
                                        <TableCell sx={{ fontWeight: 500, fontSize: '0.875rem' }}>{row.professional}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="bold">{row.caseTitle}</Typography>
                                            <Typography variant="caption" color="text.primary">ID: {row.caseId}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography color="success.main" fontWeight="bold">₹{row.amount}</Typography>
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.75rem' }}>{dayjs(row.paymentDate).format("DD MMM YYYY")}</TableCell>
                                        <TableCell sx={{ fontSize: '0.75rem' }}>
                                            {dayjs(row.completionDate).format("DD MMM YYYY")}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Box>
    );
};

export default SuperAdminApproval;